export type SourceTokenKind = 'comment' | 'function' | 'keyword' | 'number' | 'string' | 'variable' | 'plain';

export type SourceToken = {
  kind: SourceTokenKind;
  text: string;
};

const KEYWORDS = new Set([
  'async',
  'await',
  'const',
  'else',
  'finally',
  'for',
  'function',
  'if',
  'let',
  'return',
  'try',
]);

const VARIABLES = new Set(['context', 'count', 'listener', 'Math', 'Promise', 'qrScanner', 'robots', 'String']);

const classifyIdentifier = (identifier: string, rest: string): SourceTokenKind => {
  if (KEYWORDS.has(identifier)) return 'keyword';
  if (VARIABLES.has(identifier)) return 'variable';
  if (/^\s*\(/.test(rest)) return 'function';
  return 'plain';
};

/** 生成器が出力する限定的な JavaScript 構文を、React のテキストノード用に分割する。 */
export function tokenizeGeneratedSource(line: string): SourceToken[] {
  const tokens: SourceToken[] = [];
  let index = 0;

  const push = (kind: SourceTokenKind, text: string) => {
    if (text) tokens.push({ kind, text });
  };

  while (index < line.length) {
    const rest = line.slice(index);
    if (rest.startsWith('//')) {
      push('comment', rest);
      break;
    }

    const whitespace = rest.match(/^\s+/)?.[0];
    if (whitespace) {
      push('plain', whitespace);
      index += whitespace.length;
      continue;
    }

    if (line[index] === "'") {
      let end = index + 1;
      while (end < line.length) {
        if (line[end] === '\\') {
          end += 2;
          continue;
        }
        end += 1;
        if (line[end - 1] === "'") break;
      }
      push('string', line.slice(index, end));
      index = end;
      continue;
    }

    const number = rest.match(/^-?(?:\d+\.\d+|\d+|\.\d+)/)?.[0];
    if (number) {
      push('number', number);
      index += number.length;
      continue;
    }

    const identifier = rest.match(/^[A-Za-z_$][\w$]*/)?.[0];
    if (identifier) {
      push(classifyIdentifier(identifier, rest.slice(identifier.length)), identifier);
      index += identifier.length;
      continue;
    }

    const plain = rest.match(/^[^\s'A-Za-z_$0-9.-]+/)?.[0] ?? line[index];
    push('plain', plain);
    index += plain.length;
  }

  return tokens;
}
